import { useState, useEffect } from "react";
import "./Customerworkshopview.css";
import bgImage from "../../../Assets/WorkshopForm/Workshop_Dashboard1.png";
import DoAndPayment from "./Doandpayment";
import RequireServices from "./RequireServices";
import ClaimForm from "./ClaimForm";
import ViewForm from "./ViewForm";
// ─── React Query hook ────────────────────────────────────────────────────────
import { useCaseStatus, useDismantleDetail, useSurveyorDetail, useWorkshopDetail, useDocuments } from "./workshopHooks";

// ─── Constants — step config + merge helper ───────────────────────────────────
import { makeDefaultSteps, mergeApiSteps } from "./workshopConstants";

// ═══════════════════════════════════════════════════════════════
// THEME HELPERS
// ═══════════════════════════════════════════════════════════════

const getIsDark = () => {
  try { return localStorage.getItem("cw-theme") === "dark"; } catch { return false; }
};

const applyTheme = (isDark) => {
  try { localStorage.setItem("cw-theme", isDark ? "dark" : "light"); } catch {}
  document.body.classList.toggle("theme-dark",  isDark);
  document.body.classList.toggle("theme-light", !isDark);
};

// ═══════════════════════════════════════════════════════════════
// SHARED UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════

const Bg = () => (
  <>
    <div className="cw-bg-img" style={{ backgroundImage: `url(${bgImage})` }} />
    <div className="cw-bg-overlay" />
  </>
);

const Toggle = ({ isDark, onToggle }) => (
  <button className="cw-toggle" onClick={onToggle} title={isDark ? "Light mode" : "Dark mode"}>
    <div className="cw-toggle-track">
      <div className="cw-toggle-knob">{isDark ? "🌙" : "☀️"}</div>
    </div>
    <span className="cw-toggle-label">{isDark ? "DARK" : "LIGHT"}</span>
  </button>
);

const Btn = ({ children, onClick, cls = "cw-btn-primary", sx }) => (
  <button className={`cw-btn ${cls}`} onClick={onClick} style={sx}>{children}</button>
);

const Badge = ({ id }) => <span className="cw-badge cw-mono">{id}</span>;

// ═══════════════════════════════════════════════════════════════
// PANEL COMPONENTS  (right-side detail panel)
// ═══════════════════════════════════════════════════════════════

const PanelTop = ({ step, isDark, onClose }) => (
  <div style={{
    padding: "22px 20px 18px",
    borderLeft: `4px solid ${step.color}`,
    background: isDark
      ? `linear-gradient(135deg, ${step.color}14, transparent)`
      : `linear-gradient(135deg, ${step.color}10, rgba(255,255,255,0))`,
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(99,102,241,0.08)",
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div style={{ flex: 1, paddingRight: 12 }}>
        <div style={{ fontSize: 30, marginBottom: 8 }}>{step.emoji}</div>
        <div className="cw-syne" style={{ fontSize: 17, fontWeight: 800, color: step.color, marginBottom: 5 }}>{step.label}</div>
        <div className="cw-sub"  style={{ fontSize: 13, lineHeight: 1.6 }}>{step.msg}</div>
      </div>
      <button onClick={onClose} style={{
        width: 32, height: 32, borderRadius: 9, cursor: "pointer", fontSize: 13,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(99,102,241,0.08)",
        border:     isDark ? "1px solid rgba(255,255,255,0.1)" : "1.5px solid rgba(99,102,241,0.15)",
        color:      isDark ? "rgba(255,255,255,0.5)" : "#6366f1",
      }}>✕</button>
    </div>
  </div>
);

const PendingState = ({ emoji, text }) => (
  <div className="cw-pending">
    <span style={{ fontSize: 38 }}>{emoji}</span>
    <span className="cw-syne cw-sub" style={{ fontSize: 15, fontWeight: 700 }}>Not Updated Yet</span>
    <span className="cw-muted" style={{ fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>{text}</span>
  </div>
);

const InfoCard = ({ rows }) => (
  <div className="cw-info-card">
    {rows.map(([label, value, extra]) =>
      value != null && value !== "" ? (
        <div key={label} className="cw-info-row">
          <span className="cw-info-label">{label}</span>
          <span className="cw-info-value"
            style={{ color: extra?.color, fontFamily: extra?.mono ? "'DM Mono',monospace" : "inherit" }}>
            {value}
          </span>
        </div>
      ) : null
    )}
  </div>
);

const StatusBadge = ({ ok, label }) => (
  <div className={`cw-status-badge ${ok ? "cw-status-ok" : "cw-status-warn"}`}>{label}</div>
);

const Timestamp = ({ ts }) => ts ? (
  <div className="cw-muted cw-mono" style={{ fontSize: 11, textAlign: "right", marginTop: 8 }}>
    Updated {new Date(ts).toLocaleString()}
  </div>
) : null;

// ─── Payment panel ────────────────────────────────────────────
const PaymentPanel = ({ step, onClose, isDark }) => {
  const net = step.completed
    ? ((+step.amount||0) + (+step.gst||0) - (+step.salvage||0) - (+step.tds||0)).toFixed(2)
    : null;
  return (
    <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PanelTop step={step} isDark={isDark} onClose={onClose} />
      <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
        {!step.completed
          ? <PendingState emoji="💳" text="Payment details will appear once processed by our team." />
          : <>
            <StatusBadge ok={step.paymentStatus === "Paid"}
              label={step.paymentStatus === "Paid" ? "✅ Payment Confirmed" : step.paymentStatus || "Pending"} />
            <div className="cw-info-card" style={{ overflow:"hidden" }}>
              <div style={{
                display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"12px 14px",
                background: isDark ? "rgba(217,119,6,0.06)" : "rgba(245,158,11,0.05)",
                borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(99,102,241,0.08)",
              }}>
                <span className="cw-muted cw-mono" style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:2 }}>
                  Payment Receipt
                </span>
                {step.invoiceNo && <span className="cw-mono" style={{ color:step.color, fontSize:12 }}>#{step.invoiceNo}</span>}
              </div>
              <InfoCard rows={[["Mode", step.mode], ["Reference / UTR", step.refNo], ["Payment Date", step.paymentDate]]} />
              <div style={{ height:1, background: isDark?"rgba(255,255,255,0.05)":"rgba(99,102,241,0.08)" }} />
              {[
                ["Claim Amount",      step.amount  ? `₹ ${step.amount}`    : null, isDark?"#e2e8f0":"#1e1b4b"],
                ["GST",               step.gst     ? `₹ ${step.gst}`       : null, isDark?"#e2e8f0":"#1e1b4b"],
                ["Salvage Deduction", step.salvage ? `- ₹ ${step.salvage}` : null, "#ef4444"],
                ["TDS Deduction",     step.tds     ? `- ₹ ${step.tds}`     : null, "#ef4444"],
              ].map(([l,v,c]) => v && (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"10px 14px",
                  borderBottom: isDark?"1px solid rgba(255,255,255,0.04)":"1px solid rgba(99,102,241,0.06)" }}>
                  <span className="cw-sub" style={{ fontSize:12 }}>{l}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:c }}>{v}</span>
                </div>
              ))}
              <div style={{ display:"flex", justifyContent:"space-between", padding:"16px 14px",
                background: isDark?"rgba(217,119,6,0.07)":"rgba(245,158,11,0.06)",
                borderTop:  isDark?"1px solid rgba(217,119,6,0.15)":"1.5px solid rgba(245,158,11,0.15)" }}>
                <span className="cw-syne" style={{ fontSize:15, fontWeight:800, color:step.color }}>Net Payable</span>
                <span className="cw-syne" style={{ fontSize:24, fontWeight:900, color:step.color }}>₹ {net}</span>
              </div>
            </div>
            <Timestamp ts={step.updatedAt} />
          </>
        }
      </div>
    </div>
  );
};

// ─── Driver Mail panel ────────────────────────────────────────
const DriverMailPanel = ({ step, caseData, onClose, isDark }) => (
  <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
    <PanelTop step={step} isDark={isDark} onClose={onClose} />
    <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
      {!step.completed
        ? <PendingState emoji="📬" text="Our team will notify your driver once the vehicle is ready." />
        : <>
          <StatusBadge ok={step.mailStatus === "Sent"}
            label={step.mailStatus === "Sent" ? "✉️ Mail Sent to Driver" : "⏳ Pending"} />
          <div style={{
            display:"flex", gap:14, alignItems:"flex-start",
            borderRadius:14, padding:16, marginBottom:12,
            background: isDark?"rgba(239,68,68,0.06)":"rgba(239,68,68,0.05)",
            border:     isDark?"1px solid rgba(239,68,68,0.15)":"1.5px solid rgba(239,68,68,0.12)",
          }}>
            <span style={{ fontSize:28, flexShrink:0 }}>🚛</span>
            <p className="cw-sub" style={{ fontSize:14, lineHeight:1.7 }}>
              Your vehicle repair is complete. Please send your driver to{" "}
              <strong style={{ color:step.color }}>{caseData.workshopName || "the workshop"}</strong>{" "}
              with original documents to collect the truck.
            </p>
          </div>
          <Timestamp ts={step.updatedAt} />
        </>
      }
    </div>
  </div>
);

// ─── Generic step panel ───────────────────────────────────────
const GenericStepPanel = ({ step, onClose, isDark }) => (
  <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
    <PanelTop step={step} isDark={isDark} onClose={onClose} />
    <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
      {!step.completed
        ? <PendingState emoji="⏳" text="Our team will update this step shortly. Check back soon." />
        : <>
          <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:14,
            background:`${step.color}10`, border:`1.5px solid ${step.color}28` }}>
            <span style={{ fontSize:14, fontWeight:700, color:step.color }}>{step.emoji} Step Completed</span>
          </div>
          <InfoCard rows={[
            ["Completed On", step.completedAt ? new Date(step.completedAt).toLocaleString() : null],
            ["Updated By",   step.doneBy],
            ["Notes",        step.notes],
          ]} />
          <Timestamp ts={step.updatedAt} />
        </>
      }
    </div>
  </div>
);

const STATUS_LABELS = {
  ASSIGNED:    { label: "Surveyor Assigned",   color: "#8b5cf6", icon: "🔍" },
  COMPLETED:   { label: "Survey Completed",     color: "#10b981", icon: "✅" },
  RESCHEDULED: { label: "Survey Rescheduled",   color: "#f59e0b", icon: "🔄" },
  REJECTED:    { label: "Survey Rejected",      color: "#ef4444", icon: "❌" },
};

const DOC_STATUS = {
  PENDING:  { label: "Pending",  color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)",  icon: "⏳" },
  APPROVED: { label: "Approved", color: "#10b981", bg: "rgba(16,185,129,0.12)",  border: "rgba(16,185,129,0.3)",  icon: "✅" },
  REJECTED: { label: "Rejected", color: "#ef4444", bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.3)",   icon: "❌" },
};

const DOC_TYPE_MAP = {
  rc: "RC Image", permit_5_year: "NP (5 Year)", np_1_year: "NP (1 Year)",
  fitness: "Fitness", pucc: "PUCC Image", policy: "Policy Document",
  VEHICLE_TAX: "Vehicle Tax Document", DRIVER_LICENSE_FRONT: "Driver DL Front",
  DRIVER_LICENSE_BACK: "Driver DL Back", DRIVER_AADHAAR: "Driver Aadhaar Card",
  HELPER_LICENSE_FRONT: "Co-Pilot DL Front", HELPER_LICENSE_BACK: "Co-Pilot DL Back",
  fir: "FIR Image", lr: "LR Image",
  ACCIDENT_FRONT_LEFT: "Front Left", ACCIDENT_FRONT_RIGHT: "Front Right",
  ACCIDENT_RIGHT_SIDE: "Right Side", ACCIDENT_LEFT_SIDE: "Left Side",
  ACCIDENT_BACK_LEFT: "Back Left", ACCIDENT_BACK_RIGHT: "Back Right",
};

const DocStatusPill = ({ status }) => {
  const s = DOC_STATUS[status] || DOC_STATUS.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>
      {s.icon} {s.label}
    </span>
  );
};

const DocumentsPanel = ({ step, onClose, isDark, caseId }) => {
  const { data: documents = [], isLoading, isError } = useDocuments(caseId, true);
  const counts = {
    total:    documents.length,
    pending:  documents.filter(d => d.verificationStatus === "PENDING").length,
    approved: documents.filter(d => d.verificationStatus === "APPROVED").length,
    rejected: documents.filter(d => d.verificationStatus === "REJECTED").length,
  };
  const summaryPills = [
    { label: "Total",    value: counts.total,    color: "#6366f1", bg: "rgba(99,102,241,0.1)",  border: "rgba(99,102,241,0.25)" },
    { label: "Pending",  value: counts.pending,  color: "#f59e0b", bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.25)" },
    { label: "Approved", value: counts.approved, color: "#10b981", bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.25)" },
    { label: "Rejected", value: counts.rejected, color: "#ef4444", bg: "rgba(239,68,68,0.1)",   border: "rgba(239,68,68,0.25)" },
  ];
  return (
    <div className="cw-slideIn" style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <PanelTop step={step} isDark={isDark} onClose={onClose} />
      <div style={{ flex: 1, padding: "20px 20px 28px", overflowY: "auto" }}>
        {isLoading && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(99,102,241,0.08)", border:"1.5px solid rgba(99,102,241,0.2)", color:"#6366f1" }}>Loading documents…</div>}
        {isError   && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.25)", color:"#dc2626" }}>Failed to load documents.</div>}
        {!isLoading && !isError && documents.length === 0 && <PendingState emoji="📂" text="No documents have been uploaded for this case yet." />}
        {!isLoading && !isError && documents.length > 0 && (
          <>
            <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
              {summaryPills.map(({ label, value, color, bg, border }) => (
                <div key={label} style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"8px 14px", borderRadius:12, minWidth:60, background:bg, border:`1.5px solid ${border}` }}>
                  <span className="cw-syne" style={{ fontSize:20, fontWeight:800, color, lineHeight:1 }}>{value}</span>
                  <span className="cw-mono cw-muted" style={{ fontSize:9, textTransform:"uppercase", letterSpacing:1.5, marginTop:3 }}>{label}</span>
                </div>
              ))}
            </div>
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>📄 Uploaded Documents</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {documents.map((doc) => {
                const s = DOC_STATUS[doc.verificationStatus] || DOC_STATUS.PENDING;
                const typeLabel = DOC_TYPE_MAP[doc.documentType] || doc.documentType;
                const uploadedDate = doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null;
                return (
                  <div key={doc.id} style={{ borderRadius:14, overflow:"hidden", background:isDark?"rgba(255,255,255,0.025)":"rgba(255,255,255,0.9)", border:`1.5px solid ${s.border}`, boxShadow:isDark?"none":`0 2px 12px ${s.bg}`, transition:"box-shadow 0.2s" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:isDark?"rgba(255,255,255,0.03)":s.bg, borderBottom:`1px solid ${s.border}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:18 }}>📄</span>
                        <span className="cw-syne" style={{ fontSize:13, fontWeight:700, color:isDark?"#e2e8f0":"#1e1b4b" }}>{typeLabel}</span>
                      </div>
                      <DocStatusPill status={doc.verificationStatus} />
                    </div>
                    <div style={{ padding:"10px 14px" }}>
                      <InfoCard rows={[["Uploaded By", doc.uploadedBy], ["Uploaded On", uploadedDate], ["Case No.", doc.caseId]]} />
                      {doc.fileUrl && (
                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:10, padding:"7px 14px", borderRadius:10, fontSize:12, fontWeight:700, textDecoration:"none", cursor:"pointer", background:isDark?"rgba(99,102,241,0.12)":"rgba(99,102,241,0.08)", color:"#6366f1", border:"1.5px solid rgba(99,102,241,0.25)" }}>
                          🔗 View Document
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SurveyorPanel = ({ step, onClose, isDark, caseId, surveyType }) => {
  const { data: surveyors, isLoading, isError } = useSurveyorDetail(caseId, true);
  const survey = surveyors?.find(s => s.surveyType === surveyType);
  const pendingText = surveyType === "SPOT_SURVEY" ? "A spot surveyor will be assigned shortly by our team." : "A workshop surveyor will be assigned once repairs begin.";
  return (
    <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PanelTop step={step} isDark={isDark} onClose={onClose} />
      <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
        {isLoading && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(139,92,246,0.08)", border:"1.5px solid rgba(139,92,246,0.2)", color:"#8b5cf6" }}>Loading surveyor details…</div>}
        {isError   && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.25)", color:"#dc2626" }}>Failed to load surveyor details.</div>}
        {!isLoading && !isError && !survey && <PendingState emoji="🔍" text={pendingText} />}
        {survey && (() => {
          const statusInfo = STATUS_LABELS[survey.surveyStatus] || { label: survey.surveyStatus, color: "#6366f1", icon: "🔍" };
          return (
            <>
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, marginBottom:14, background:`${statusInfo.color}15`, color:statusInfo.color, border:`1.5px solid ${statusInfo.color}30` }}>
                {statusInfo.icon} {statusInfo.label}
              </div>
              <div style={{ display:"flex", gap:14, alignItems:"center", borderRadius:14, padding:"14px 16px", marginBottom:14, background:isDark?"rgba(139,92,246,0.08)":"rgba(139,92,246,0.05)", border:isDark?"1px solid rgba(139,92,246,0.2)":"1.5px solid rgba(139,92,246,0.15)" }}>
                <span style={{ fontSize:34, flexShrink:0 }}>🕵️</span>
                <div>
                  <div className="cw-syne" style={{ fontSize:16, fontWeight:800, color:step.color }}>{survey.surveyorName || "—"}</div>
                  <div className="cw-mono" style={{ fontSize:11, marginTop:2, color:statusInfo.color }}>{survey.surveyType?.replace("_", " ")}</div>
                </div>
              </div>
              <InfoCard rows={[
                ["Assigned By", survey.assignedBy],
                ["Scheduled On", survey.scheduledDate ? new Date(survey.scheduledDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null],
                ["Completed On", survey.surveyCompletedDate ? new Date(survey.surveyCompletedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
                ["Remarks", survey.remarks],
                ["Rejection Reason", survey.rejectionReason],
                ["Reschedule Reason", survey.rescheduleReason],
              ]} />
              <Timestamp ts={survey.createdAt} />
            </>
          );
        })()}
      </div>
    </div>
  );
};

const WorkshopPanel = ({ step, onClose, isDark, caseId }) => {
  const { data: workshop, isLoading, isError } = useWorkshopDetail(caseId, true);
  const fullAddress = [workshop?.address, workshop?.district, workshop?.state, workshop?.pincode].filter(Boolean).join(", ");
  const estimateStatus = workshop?.estimateApprovedDate
    ? { label: "Estimate Approved", color: "#10b981", icon: "✅" }
    : { label: "Estimate Pending", color: "#f59e0b", icon: "⏳" };
  return (
    <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PanelTop step={step} isDark={isDark} onClose={onClose} />
      <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
        {isLoading && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.2)", color:"#d97706" }}>Loading workshop details…</div>}
        {isError   && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.25)", color:"#dc2626" }}>Failed to load workshop details.</div>}
        {!isLoading && !isError && !workshop && <PendingState emoji="🔧" text="A workshop will be assigned shortly by our team." />}
        {workshop && (
          <>
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, marginBottom:14, background:`${estimateStatus.color}15`, color:estimateStatus.color, border:`1.5px solid ${estimateStatus.color}30` }}>
              {estimateStatus.icon} {estimateStatus.label}
            </div>
            <div style={{ display:"flex", gap:14, alignItems:"center", borderRadius:14, padding:"14px 16px", marginBottom:14, background:isDark?"rgba(245,158,11,0.08)":"rgba(245,158,11,0.05)", border:isDark?"1px solid rgba(245,158,11,0.2)":"1.5px solid rgba(245,158,11,0.15)" }}>
              <span style={{ fontSize:34, flexShrink:0 }}>🔧</span>
              <div>
                <div className="cw-syne" style={{ fontSize:16, fontWeight:800, color:step.color }}>{workshop.vendorName || "—"}</div>
                {fullAddress && <div className="cw-muted" style={{ fontSize:11, marginTop:3, lineHeight:1.5 }}>📍 {fullAddress}</div>}
              </div>
            </div>
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Contact</div>
            <InfoCard rows={[["Phone", workshop.vendorPhone], ["Email", workshop.email], ["Contact No.", workshop.contactPersonNum]]} />
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", margin:"14px 0 8px" }}>Timeline</div>
            <InfoCard rows={[
              ["Assigned On", workshop.workshopAssignedDate ? new Date(workshop.workshopAssignedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null],
              ["Work Started", workshop.workStartedDate ? new Date(workshop.workStartedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
              ["Estimate Approved", workshop.estimateApprovedDate ? new Date(workshop.estimateApprovedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
              ["Dismantling Approved", workshop.dismantlingApprovedDate ? new Date(workshop.dismantlingApprovedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
              ["Dismantling Completed", workshop.dismantlingCompletedDate ? new Date(workshop.dismantlingCompletedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
            ]} />
          </>
        )}
      </div>
    </div>
  );
};

const DismantlePanel = ({ step, onClose, isDark, caseId }) => {
  const { data: dismantle, isLoading, isError } = useDismantleDetail(caseId, true);
  const isCompleted = !!dismantle?.dismantlingCompletedDate;
  const statusInfo = isCompleted
    ? { label: "Dismantling Completed",   color: "#10b981", icon: "✅" }
    : dismantle?.dismantlingApprovedDate
    ? { label: "Dismantling In Progress", color: "#f59e0b", icon: "🔄" }
    : { label: "Awaiting Approval",       color: "#6366f1", icon: "⏳" };
  return (
    <div className="cw-slideIn" style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PanelTop step={step} isDark={isDark} onClose={onClose} />
      <div style={{ flex:1, padding:"20px 20px 28px", overflowY:"auto" }}>
        {isLoading && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.2)", color:"#ef4444" }}>Loading dismantle details…</div>}
        {isError   && <div style={{ padding:"12px 16px", borderRadius:12, fontSize:13, background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.25)", color:"#dc2626" }}>Failed to load dismantle details.</div>}
        {!isLoading && !isError && !dismantle && <PendingState emoji="✂️" text="Dismantling has not been initiated yet." />}
        {dismantle && (
          <>
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, marginBottom:14, background:`${statusInfo.color}15`, color:statusInfo.color, border:`1.5px solid ${statusInfo.color}30` }}>
              {statusInfo.icon} {statusInfo.label}
            </div>
            <div style={{ display:"flex", gap:14, alignItems:"center", borderRadius:14, padding:"14px 16px", marginBottom:14, background:isDark?"rgba(239,68,68,0.08)":"rgba(239,68,68,0.05)", border:isDark?"1px solid rgba(239,68,68,0.2)":"1.5px solid rgba(239,68,68,0.15)" }}>
              <span style={{ fontSize:34, flexShrink:0 }}>✂️</span>
              <div>
                <div className="cw-syne" style={{ fontSize:16, fontWeight:800, color:step.color }}>Dismantling</div>
                <div className="cw-mono" style={{ fontSize:11, marginTop:2, color:statusInfo.color }}>{statusInfo.label}</div>
              </div>
            </div>
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Timeline</div>
            <InfoCard rows={[
              ["Approved On",  dismantle.dismantlingApprovedDate  ? new Date(dismantle.dismantlingApprovedDate).toLocaleString("en-IN",  { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null],
              ["Completed On", dismantle.dismantlingCompletedDate ? new Date(dismantle.dismantlingCompletedDate).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null],
              ["Final Approval",dismantle.finalApprovalDate       ? new Date(dismantle.finalApprovalDate).toLocaleString("en-IN",       { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : null],
            ]} />
          </>
        )}
      </div>
    </div>
  );
};

const BooleanPanel = ({ step, onClose, isDark }) => (
  <div className="cw-slideIn">
    <PanelTop step={step} isDark={isDark} onClose={onClose} />
    <div style={{ padding: 20 }}>
      <InfoCard rows={[["Status", step.completed ? "Done ✅" : "Not Done ❌"]]} />
    </div>
  </div>
);

// ─── Panel router ─────────────────────────────────────────────
const DetailPanel = ({ step, caseData, isDark, onClose, onStepComplete }) => {
  if (!step) return null;
  console.log('Rendering DetailPanel for step:', caseData.accidentCaseNo, step.id, step);
  if (step.id === "docs")          return <DocumentsPanel step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo} />;
  if (step.id === "payment")       return <DoAndPayment amount={step.amount} onClose={onClose} isDark={isDark} />;
  if (step.id === "services")      return <RequireServices step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo} onStepComplete={onStepComplete} />;
  if (step.id === "claimform")     return <ClaimForm step={step} onClose={onClose} isDark={isDark} caseData={caseData} />;
  if (step.special === "drivermail") return <DriverMailPanel step={step} caseData={caseData} onClose={onClose} isDark={isDark} />;
  if (step.id === "surveyor")      return <SurveyorPanel step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo} surveyType="SPOT_SURVEY" />;
  if (step.id === "workshopsurvey") return <SurveyorPanel step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo} surveyType="WORKSHOP_SURVEY" />;
  if (step.id === "workshop")      return <WorkshopPanel step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo || caseData.id} />;
  if (step.id === "dismantle")     return <DismantlePanel step={step} onClose={onClose} isDark={isDark} caseId={caseData.accidentCaseNo} />;
  if (step.id === "secondsurvey" || step.id === "reinspection" || step.id === "paperwork" || step.id === "roadtrial" || step.id === "claim")
    return <BooleanPanel step={step} onClose={onClose} isDark={isDark} />;
  return <GenericStepPanel step={step} onClose={onClose} isDark={isDark} />;
};

// ═══════════════════════════════════════════════════════════════
// MAIN — CustomerWorkshopView
// ═══════════════════════════════════════════════════════════════

const CustomerWorkshopView = ({ caseData, onBack }) => {

  const [active, setActive] = useState(null);
  const [mob,    setMob]    = useState(typeof window !== "undefined" && window.innerWidth < 900);
  const [stepOverrides, setStepOverrides] = useState({});

  const handleStepComplete = (stepId, extraData = {}) => {
    setStepOverrides(prev => ({ ...prev, [stepId]: { completed: true, ...extraData } }));
  };

  const [isDark, setIsDark] = useState(getIsDark);
  useEffect(() => {
    applyTheme(isDark);
    const onResize = () => setMob(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isDark]);

  const caseId = caseData.accidentCaseNo || caseData.id;
  const { data: statusData, isLoading: loading, isError: isStatusError, error: statusError } = useCaseStatus(caseId);
  const { data: docs = [] }  = useDocuments(caseId, !!caseId);
  const { data: surveyors }  = useSurveyorDetail(caseId, !!caseId);
  const { data: workshop }   = useWorkshopDetail(caseId, !!caseId);
  const { data: dismantle }  = useDismantleDetail(caseId, !!caseId);

  const currentStatus = caseData.currentStatus;
  const steps = mergeApiSteps(statusData?.steps || [], currentStatus, caseData, docs, surveyors || [], workshop || null, dismantle || null)
    .map(s => stepOverrides[s.id] ? { ...s, ...stepOverrides[s.id] } : s);

  const toggleTheme = () => setIsDark((p) => !p);
  const openPanel   = (id) => setActive((prev) => prev === id ? null : id);
  const closePanel  = () => setActive(null);

  const done      = steps.filter((s) => s.completed).length;
  const pct       = steps.length > 0 ? Math.round((done / steps.length) * 100) : 0;
  const curI      = steps.findIndex((s) => !s.completed);
  const panelOpen = active !== null;
  const activeStep = panelOpen && active !== "form" ? steps.find((s) => s.id === active) : null;
  const R = 46, C = 2 * Math.PI * R;

  // ─── SUBTLE COLORS — only these 3 vars changed from original ───
  // Ring: single muted purple instead of rainbow gradient
  const ringColor = isDark ? "#8b87cc" : "#7F77DD";
  const ringTrack = isDark ? "rgba(255,255,255,0.06)" : "rgba(127,119,221,0.1)";

  return (
    <div className="cw-page" style={{ overflow:"hidden" }}>
      <Bg />

      {/* ── Navigation ── */}
      <nav className="cw-nav">
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Btn cls={isDark?"cw-btn-ghost-d":"cw-btn-ghost-l"} onClick={onBack}
            sx={{ padding:"7px 14px", fontSize:13 }}>← Back</Btn>
        </div>
        <div className="cw-nav-cid" style={{ display:"flex", alignItems:"center", gap:7 }}>
          <span className="cw-muted" style={{ fontSize:9, textTransform:"uppercase", letterSpacing:2 }}>Case</span>
          <Badge id={caseData.accidentCaseNo || caseData.id} />
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Toggle isDark={isDark} onToggle={toggleTheme} />
          <Btn
            cls={active === "form" ? "cw-btn-danger" : "cw-btn-cyan"}
            onClick={() => openPanel("form")}
            sx={{ fontSize:13, padding:"8px 14px" }}
          >
            {active === "form" ? "✕ Close" : "📋 View Form"}
          </Btn>
        </div>
      </nav>

      {/* ── Split layout ── */}
      <div className="cw-split" style={{
        gridTemplateColumns: panelOpen && !mob ? "1fr 400px" : "1fr",
        height: "calc(100vh - 62px)",
      }}>

        {/* LEFT */}
        <div className="cw-scroll">

          {loading && (
            <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:12, fontSize:13,
              background:"rgba(99,102,241,0.08)", border:"1.5px solid rgba(99,102,241,0.25)", color:"#4f46e5" }}>
              Loading repair status…
            </div>
          )}
          {isStatusError && (
            <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:12, fontSize:13,
              background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.25)", color:"#dc2626" }}>
              {statusError?.message || "Failed to load repair status."}
            </div>
          )}

          {/* ── Hero ── */}
          <div className="cw-hero cw-fadeUp">
            <div className="cw-hero-bar" />
            <div className="cw-hero-row" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 22px 12px" }}>
              <div>
                <div className="cw-syne cw-text" style={{ fontSize:20, fontWeight:800, marginBottom:3 }}>
                  {caseData.vehicleNumber || caseData.accidentCaseNo || "Vehicle"}
                </div>
                {/* Muted purple instead of bright accent */}
                <div className="cw-mono" style={{ fontSize:14, color: isDark ? "#8b87cc" : "#7F77DD", marginBottom:3 }}>
                  {caseData.accidentCaseNo || caseData.id || "—"}
                </div>
                <div className="cw-sub" style={{ fontSize:12, marginBottom:13 }}>
                  🧑‍✈️ {caseData.driverName || "—"}
                </div>
                {done === steps.length
                  ? <span style={{ fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, background:"rgba(16,185,129,0.1)", color:"#10b981", border:"1.5px solid rgba(16,185,129,0.2)" }}>✅ Repair Complete</span>
                  : <span style={{ fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, background:"rgba(245,158,11,0.1)", color:"#d97706", border:"1.5px solid rgba(245,158,11,0.2)" }}>🔄 In Progress</span>
                }
              </div>

              {/* ── Ring: single muted color, no rainbow gradient ── */}
              <div className="cw-ring" style={{ flexShrink:0 }}>
                <svg width={108} height={108} viewBox="0 0 110 110" aria-label={`${pct}% complete`}>
                  <circle cx={55} cy={55} r={R} fill="none" stroke={ringTrack} strokeWidth={9} />
                  <circle cx={55} cy={55} r={R} fill="none" stroke={ringColor} strokeWidth={9}
                    strokeDasharray={`${(pct/100)*C} ${C}`} strokeLinecap="round"
                    transform="rotate(-90 55 55)"
                    style={{ transition:"stroke-dasharray 1s cubic-bezier(.16,1,.3,1)" }} />
                  <text x={55} y={51} textAnchor="middle" fill={isDark?"#f1f5f9":"#1e1b4b"} fontSize={22} fontWeight={800} fontFamily="Outfit,sans-serif">{pct}%</text>
                  <text x={55} y={66} textAnchor="middle" fill={isDark?"rgba(255,255,255,0.35)":"rgba(30,27,75,0.4)"} fontSize={10} fontFamily="Outfit,sans-serif">Complete</text>
                  <text x={55} y={79} textAnchor="middle" fill={isDark?"rgba(255,255,255,0.22)":"rgba(30,27,75,0.3)"} fontSize={9}  fontFamily="DM Mono,monospace">{done}/{steps.length}</text>
                </svg>
              </div>
            </div>

            {/* ── Progress bar: soft two-tone, no shimmer animation ── */}
            <div style={{ height:4, background:isDark?"rgba(255,255,255,0.05)":"rgba(127,119,221,0.1)", margin:"0 22px 10px", borderRadius:2 }}>
              <div style={{
                height:"100%", borderRadius:2, width:`${pct}%`,
                background: "linear-gradient(90deg, #7F77DD, #5DCAA5)",
                transition:"width 1s cubic-bezier(.16,1,.3,1)",
                // No shimmer animation
              }} />
            </div>

            <div className="cw-hero-footer cw-muted" style={{ display:"flex", justifyContent:"space-between", padding:"0 22px 14px", fontSize:12 }}>
              <span>
                {curI >= 0
                  ? <>Current: <span style={{ color: isDark ? "#8b87cc" : "#534AB7", fontWeight:700 }}>{steps[curI]?.label}</span></>
                  : <span style={{ color:"#10b981", fontWeight:700 }}>All steps complete ✓</span>
                }
              </span>
              {caseData.createdAt && <span className="cw-mono">📅 {caseData.createdAt.slice(0,10)}</span>}
            </div>
          </div>

          {/* ── Dot strip: step color at 80% opacity done, 50% active, flat gray pending, no glow ── */}
          <div className="cw-dot-strip cw-fadeUp">
            {steps.map((s, i) => (
              <div key={s.id} className="cw-dot" title={s.label} onClick={() => openPanel(s.id)}
                style={{
                  background: s.completed
                    ? s.color + "CC"
                    : i === curI
                    ? s.color + "77"
                    : isDark ? "rgba(255,255,255,0.12)" : "#D8D6CF",
                  boxShadow: "none",
                  transform: i === curI ? "scale(1.5)" : s.completed ? "scale(1.1)" : "scale(1)",
                  outline:   active === s.id ? `2px solid ${s.color}88` : "none",
                  outlineOffset: 2,
                }} />
            ))}
          </div>

          {/* ── Bar chart: step color at 65% opacity done, very light gray pending, no shadow ── */}
          <div className="cw-chart-wrap cw-fadeUp">
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>
              📊 Pipeline Overview
            </div>
            <div style={{ display:"flex", gap:"1.5%", alignItems:"flex-end", height:64, width:"100%" }}>
              {steps.map((s, i) => {
                const BASE_H = [20, 36, 28, 48, 32, 44, 24, 40, 52, 30, 38, 26, 46, 34];
                const base = BASE_H[i % BASE_H.length];
                const h    = s.completed ? Math.min(base + 12, 58) : active === s.id ? Math.min(base + 5, 58) : base;
                const sel  = active === s.id;
                return (
                  <div key={s.id} onClick={() => openPanel(s.id)}
                    style={{
                      flex:"1 1 0", minWidth:0, cursor:"pointer", height: h,
                      borderRadius:"4px 4px 0 0",
                      transition:"all 0.4s cubic-bezier(.16,1,.3,1)",
                      // Flat color at 65% for done, no gradient, no shadow
                      background: s.completed
                        ? s.color + "A6"
                        : sel
                        ? s.color + "55"
                        : isDark ? "rgba(255,255,255,0.09)" : "#E8E6E0",
                      boxShadow: "none",
                      border: sel ? `1px solid ${s.color}55` : "1px solid transparent",
                    }}
                  />
                );
              })}
            </div>
            <div style={{ display:"flex", gap:"1.5%", width:"100%", marginTop:4 }}>
              {steps.map((s) => (
                <div key={s.id} style={{ flex:"1 1 0", minWidth:0, textAlign:"center" }}>
                  <span style={{ fontSize:"clamp(8px,1.6vw,12px)", lineHeight:1, filter:s.completed?"none":"grayscale(1)", opacity:s.completed?1:0.3 }}>
                    {s.emoji}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Timeline ── */}
          <div className="cw-timeline-wrap cw-fadeUp">
            <div className="cw-mono cw-muted" style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>
              🗂 Step-by-Step Status
            </div>
            {steps.map((s, i) => {
              const isActive = i === curI;
              const selected = active === s.id;
              return (
                <div key={s.id} className="cw-tl-row" onClick={() => openPanel(s.id)} style={{
                  display:"flex", gap:10, padding:"10px 12px", borderRadius:12, border:"1.5px solid", marginBottom:3,
                  background: selected
                    ? (isDark ? `${s.color}0D` : `${s.color}08`)
                    : s.completed ? (isDark?"rgba(255,255,255,0.02)":"rgba(255,255,255,0.8)") : "transparent",
                  borderColor: selected ? `${s.color}44` : s.completed ? `${s.color}22` : isActive ? `${s.color}30` : isDark?"rgba(255,255,255,0.05)":"rgba(30,27,75,0.06)",
                  opacity: !s.completed && !isActive ? 0.5 : 1,
                  boxShadow: "none", // removed glow
                }}>

                  {/* Spine dot — 10px, no box-shadow */}
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0, paddingTop:3 }}>
                    <div style={{
                      width:10, height:10, borderRadius:"50%", flexShrink:0, transition:"all 0.3s",
                      background: s.completed ? s.color + "CC" : isActive ? s.color + "88" : isDark ? "rgba(255,255,255,0.15)" : "#D8D6CF",
                      border: `1.5px solid ${s.completed ? s.color + "99" : isActive ? s.color + "55" : isDark ? "rgba(255,255,255,0.2)" : "#C5C3BC"}`,
                      // No boxShadow
                    }} />
                    {i < steps.length-1 && (
                      <div style={{ width:2, flex:1, minHeight:12, marginTop:2, borderRadius:1,
                        background: s.completed ? s.color + "44" : isDark ? "rgba(255,255,255,0.05)" : "rgba(99,102,241,0.08)" }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                      <span style={{ fontSize:17, flexShrink:0, filter:!s.completed&&!isActive?"grayscale(1)":"none", opacity:!s.completed&&!isActive?0.4:1 }}>
                        {s.emoji}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3,
                          color: s.completed ? s.color : isActive ? s.color : isDark?"#94a3b8":"rgba(30,27,75,0.5)" }}>
                          {s.label}
                        </div>
                        {s.completed && s.notes && (
                          <div className="cw-note-hide cw-muted" style={{ fontSize:11, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.notes}</div>
                        )}
                        {s.completed && s.completedAt && (
                          <div className="cw-mono cw-muted" style={{ fontSize:10, marginTop:2 }}>
                            {new Date(s.completedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                          </div>
                        )}
                      </div>

                      {/* Badge — very light tinted bg, not solid fill */}
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3, flexShrink:0 }}>
                        {s.completed
                          ? <span className="cw-mono" style={{ fontSize:9, padding:"2px 8px", borderRadius:6, fontWeight:700,
                              background: s.color + "15", color: s.color, border: `1px solid ${s.color}30` }}>Done ✓</span>
                          : isActive
                          ? <span className="cw-mono" style={{ fontSize:9, padding:"2px 8px", borderRadius:6, fontWeight:700,
                              background: s.color + "15", color: s.color, border: `1px solid ${s.color}30` }}>● Now</span>
                          : <span className="cw-mono cw-muted" style={{ fontSize:9, padding:"2px 6px", borderRadius:6,
                              background:isDark?"rgba(255,255,255,0.04)":"rgba(30,27,75,0.05)",
                              border:`1px solid ${isDark?"rgba(255,255,255,0.07)":"rgba(30,27,75,0.08)"}` }}>#{i+1}</span>
                        }
                        <span className="cw-muted" style={{ fontSize:9, color:selected?s.color+"99":undefined }}>
                          {selected?"◀":"▶"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>{/* end LEFT */}

        {/* RIGHT — detail panel */}
        {panelOpen && (
          <div className="cw-panel">
            {active === "form" ? (
              <ViewForm caseData={caseData} isDark={isDark} onClose={closePanel} />
            ) : (
              <DetailPanel step={activeStep} caseData={caseData} isDark={isDark} onClose={closePanel} onStepComplete={handleStepComplete} />
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomerWorkshopView;